import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import Button from '../../../components/Button';
import Input from '../../../components/Input';
import Dropdown from '../../../components/Dropdown';
import StatusBadge from '../../../components/StatusBadge';
import RowActions from '../../../components/RowActions';
import Pagination from '../../../components/TableBox/Pagination';
import LoadingRow from '../../../components/TableBox/Rows/LoadingRow';
import Toast from '../../../components/Toast';
import NotificationService, { EmailOutboxEntry, EmailOutboxStatus } from '../../../services/notification.service';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';
import { SortOrder } from '../../../enums';

const STATUS_OPTIONS: EmailOutboxStatus[] = ['pending', 'sent', 'failed'];
const EVENT_TYPE_OPTIONS = ['renewal_reminder', 'receipt', 'welcome_invite', 'suspension_warning', 'expiry_notice'];

const STATUS_COLOUR_MAP: Record<EmailOutboxStatus, string> = {
  sent: '#22c55e',
  failed: '#ef4444',
  pending: '#f59e0b',
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  renewal_reminder: 'Renewal Reminder',
  receipt: 'Receipt',
  welcome_invite: 'Welcome',
  suspension_warning: 'Suspension',
  expiry_notice: 'Expiry',
};

const OutboxLog: React.FC = () => {
  const { t } = useTranslation();

  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.desc, defaultOrderBy: 'created_at' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<EmailOutboxStatus[]>([]);
  const [eventTypeFilter, setEventTypeFilter] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<EmailOutboxEntry[]>([]);
  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const filter = useMemo(
    () => ({
      status: statusFilter.length ? statusFilter : undefined,
      eventType: eventTypeFilter.length ? eventTypeFilter : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      recipientEmail: recipientEmail || undefined,
      page,
      limit: pageSize,
    }),
    [statusFilter, eventTypeFilter, startDate, endDate, recipientEmail, page, pageSize]
  );

  const load = useCallback(() => {
    setLoading(true);
    NotificationService.listOutbox(filter)
      .then((response: any) => {
        setItems(response?.data?.items ?? []);
        setTotal(response?.data?.pagination?.total ?? 0);
      })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleClearFilters = useCallback(() => {
    setStatusFilter([]);
    setEventTypeFilter([]);
    setStartDate('');
    setEndDate('');
    setRecipientEmail('');
    setPage(1);
  }, []);

  const handleRetry = useCallback(
    async (id: number) => {
      setRetryingId(id);
      try {
        await NotificationService.retryOutbox(id);
        toast.success(<Toast message={t('notifications.outbox.pageTexts.retrySuccess')} />, { icon: false });
        load();
      } finally {
        setRetryingId(null);
      }
    },
    [load, t]
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h4 className="m-0">{t('notifications.outbox.pageTexts.title')}</h4>
          <p className="text-muted m-0">{t('notifications.outbox.pageTexts.subTitle')}</p>
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 mb-3">
        <div className="flex-grow-1">
          <Input
            id="outbox-search"
            name="recipientEmail"
            placeholder={t('notifications.outbox.pageTexts.searchPlaceholder')}
            value={recipientEmail}
            onChange={(e: any) => {
              setRecipientEmail(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <IconButton onClick={() => setFiltersOpen((v) => !v)} color={filtersOpen ? 'primary' : 'default'}>
          <FilterListIcon />
        </IconButton>
      </div>

      <Collapse in={filtersOpen}>
        <div className="row g-3 mb-3">
          <div className="col-md-2">
            <Input
              id="startDate"
              name="startDate"
              type="date"
              label={t('notifications.outbox.pageTexts.filters.startDate')}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Input
              id="endDate"
              name="endDate"
              type="date"
              label={t('notifications.outbox.pageTexts.filters.endDate')}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <Dropdown
              name="statusFilter"
              label={t('notifications.outbox.pageTexts.filters.status')}
              values={statusFilter}
              options={STATUS_OPTIONS.map((st) => ({ id: st, name: st }))}
              multi
              noSearch
              onValuesChange={(vals) => setStatusFilter(vals as EmailOutboxStatus[])}
            />
          </div>
          <div className="col-md-3">
            <Dropdown
              name="eventTypeFilter"
              label={t('notifications.outbox.pageTexts.filters.eventType')}
              values={eventTypeFilter}
              options={EVENT_TYPE_OPTIONS.map((ev) => ({ id: ev, name: EVENT_TYPE_LABELS[ev] ?? ev }))}
              multi
              noSearch
              onValuesChange={(vals) => setEventTypeFilter(vals)}
            />
          </div>
          <div className="col-md-2 d-flex align-items-center">
            <Button
              className="btn btn-outline-secondary"
              text={t('notifications.outbox.pageTexts.filters.clearAll')}
              onClick={handleClearFilters}
            />
          </div>
        </div>
      </Collapse>

      <div className="obs-table-card card">
        <div className="card-body p-0" style={{ overflowX: 'auto' }}>
          <table className="table obs-table mb-0">
            <thead>
              <tr>
                {[
                  { id: 'created_at', label: t('notifications.outbox.pageTexts.table.columns.timestamp') },
                  { id: 'event_type', label: t('notifications.outbox.pageTexts.table.columns.eventType') },
                  { id: 'recipient_email', label: t('notifications.outbox.pageTexts.table.columns.recipient') },
                  { id: 'subject', label: t('notifications.outbox.pageTexts.table.columns.subject') },
                  { id: 'status', label: t('notifications.outbox.pageTexts.table.columns.status') },
                  { id: 'attempt_count', label: t('notifications.outbox.pageTexts.table.columns.attempts') },
                  { id: 'last_attempted_at', label: t('notifications.outbox.pageTexts.table.columns.lastAttempted') },
                ].map((col) => {
                  const isSorted = orderBy === col.id;
                  return (
                    <th
                      key={col.id}
                      className={`sorting ${isSorted ? (order === SortOrder.asc ? 'sorting_asc' : 'sorting_desc') : ''}`}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => {
                        if (orderBy === col.id) {
                          setOrder(order === SortOrder.asc ? SortOrder.desc : SortOrder.asc);
                        } else {
                          setOrderBy(col.id);
                          setOrder(SortOrder.asc);
                        }
                      }}
                    >
                      <span className="d-inline-flex align-items-center gap-1 w-100">
                        <span>{col.label}</span>
                        <span style={{ opacity: isSorted ? 1 : 0.4, transition: 'opacity 0.15s ease' }}>
                          {isSorted ? (
                            order === SortOrder.asc ? (
                              <i className="ri-arrow-up-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            ) : (
                              <i className="ri-arrow-down-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            )
                          ) : (
                            <i className="ri-arrow-up-down-line" style={{ fontSize: '13px' }} />
                          )}
                        </span>
                      </span>
                    </th>
                  );
                })}
                <th className="text-end">{t('notifications.outbox.pageTexts.table.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <LoadingRow colSpan={8} />
              ) : sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-muted text-center py-4">{t('notifications.outbox.pageTexts.table.noDataFoundMessage')}</td>
                </tr>
              ) : (
                sortedItems.map((entry) => (
                  <React.Fragment key={entry.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}>
                      <td>{new Date(entry.created_at).toLocaleString()}</td>
                      <td>
                        <StatusBadge label={EVENT_TYPE_LABELS[entry.event_type] ?? entry.event_type} colour="#3b82f6" />
                      </td>
                      <td>{entry.recipient_email}</td>
                      <td>{entry.subject}</td>
                      <td>
                        <StatusBadge label={entry.status} colour={STATUS_COLOUR_MAP[entry.status]} />
                      </td>
                      <td>{entry.attempt_count}</td>
                      <td>{entry.last_attempted_at ? new Date(entry.last_attempted_at).toLocaleString() : '—'}</td>
                      <td className="text-end">
                        <RowActions
                          actions={[
                            {
                              id: 'toggle',
                              label: expandedId === entry.id ? 'Collapse Details' : 'View Details',
                              icon: <i className="ri-eye-line" />,
                              onClick: () => setExpandedId(expandedId === entry.id ? null : entry.id),
                            },
                            entry.status === 'failed' && {
                              id: 'retry',
                              label: t('common.retry') || 'Retry Sending',
                              icon: <i className="ri-refresh-line" />,
                              onClick: () => handleRetry(entry.id),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                    {expandedId === entry.id && (
                      <tr>
                        <td colSpan={8} className="bg-light">
                          <div className="p-2">
                            <div className="mb-2">
                              <div className="text-muted small mb-1">{t('notifications.outbox.pageTexts.table.columns.subject')}</div>
                              <div>{entry.subject}</div>
                            </div>
                            <div className="mb-2">
                              <div className="text-muted small mb-1">{t('notifications.outbox.pageTexts.bodyPreview')}</div>
                              <div className="small" dangerouslySetInnerHTML={{ __html: entry.rendered_body.slice(0, 500) }} />
                            </div>
                            {entry.error_message && (
                              <div>
                                <div className="text-muted small mb-1">{t('notifications.outbox.pageTexts.errorMessage')}</div>
                                <div className="small text-danger">{entry.error_message}</div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )))}
              </tbody>
            </table>
        </div>
        <Pagination
          page={page}
          total={total}
          perPage={pageSize}
          onPageChange={setPage}
          onPerPageChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default OutboxLog;
